import React from "react";
import { Link } from "react-router-dom";
import { FileText, ChevronRight, ChevronDown, ArrowUpRight } from "lucide-react";
import { COMPANY } from "../lib/policyContent";

export default function PolicyPage({ policy }) {
  const [active, setActive] = React.useState(policy.sections[0]?.id);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  React.useEffect(() => {
    document.title = `${policy.title} | PlayerReach`;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", policy.shortDesc);
  }, [policy]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    policy.sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [policy]);

  return (
    <div data-testid={`policy-page-${policy.slug}`} className="bg-white">
      {/* Header */}
      <section className="relative bg-[#0A0E1A] text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
        <div aria-hidden className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-[#0052FF]/20 blur-3xl" />
        <div className="relative container-pr py-20 lg:py-24">
          <Link to="/legal" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-cyan-300 hover:text-cyan-200" data-testid="back-to-legal">
            <FileText className="h-3.5 w-3.5" /> Legal Centre
          </Link>
          <h1 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-medium tracking-[-0.035em] max-w-3xl leading-[1.04]">{policy.title}</h1>
          <p className="mt-5 text-lg text-slate-300/90 max-w-2xl leading-relaxed">{policy.shortDesc}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Meta label="Operating company" value={COMPANY.entity} />
            <Meta label="Formation No." value={COMPANY.formation} />
            <Meta label="Version" value={COMPANY.version} />
            <Meta label="Effective date" value={COMPANY.effectiveDate} />
            {policy.isAgreement && <Meta label="Document type" value="Agreement" highlight />}
          </div>
        </div>
      </section>

      {/* Mobile section nav */}
      <div className="lg:hidden border-b border-slate-200 sticky top-16 z-30 bg-white/95 backdrop-blur">
        <button
          className="container-pr w-full flex items-center justify-between py-3 text-sm font-medium text-slate-700"
          onClick={() => setMobileNavOpen((v) => !v)}
          data-testid="policy-mobile-nav-toggle"
        >
          <span>Jump to section</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${mobileNavOpen ? "rotate-180" : ""}`} />
        </button>
        {mobileNavOpen && (
          <div className="container-pr pb-4">
            <ul className="grid grid-cols-1 gap-1">
              {policy.sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={() => setMobileNavOpen(false)}
                    className={`block px-3 py-2 rounded-md text-sm ${active === s.id ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="container-pr py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Sticky index */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28">
              <p className="overline mb-4">Sections</p>
              <ul className="space-y-1.5 border-l border-slate-200">
                {policy.sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`group flex items-start gap-2 -ml-px pl-4 py-1 border-l-2 transition-colors ${
                        active === s.id ? "border-[#0052FF] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
                      }`}
                      data-testid={`policy-nav-${s.id}`}
                    >
                      <ChevronRight className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 transition-opacity ${active === s.id ? "opacity-100 text-[#0052FF]" : "opacity-0 group-hover:opacity-50"}`} />
                      <span className="text-sm leading-snug">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mt-10 rounded-lg border border-slate-200 p-5">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Need to talk?</p>
                <p className="mt-2 text-sm text-slate-700">For policy or compliance questions, contact our team.</p>
                <a href={`mailto:${COMPANY.compliance}`} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#0052FF] hover:underline">
                  {COMPANY.compliance} <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9">
            <div className="policy-prose max-w-3xl">
              {policy.sections.map((s) => (
                <section key={s.id} id={s.id}>
                  <h2>{s.title}</h2>
                  <div dangerouslySetInnerHTML={{ __html: s.body }} />
                </section>
              ))}
            </div>

            <div className="mt-16 pt-8 border-t border-slate-200 max-w-3xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-slate-500">
                  Version {COMPANY.version} · Effective {COMPANY.effectiveDate}
                </div>
                <Link to="/legal" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0052FF] hover:underline" data-testid="policy-back-to-centre">
                  Back to Legal Centre <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, highlight }) {
  return (
    <div className={`px-3 py-2 rounded-md border ${highlight ? "border-cyan-400/40 bg-cyan-400/10" : "border-white/10 bg-white/[0.04]"}`}>
      <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-mono-pr">{label}</p>
      <p className="text-sm text-white">{value}</p>
    </div>
  );
}
